import React, { useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,  IconButton, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    TablePagination, Checkbox, Box, Typography
} from '@mui/material';
import {Delete, FilterList} from '@mui/icons-material';
import {
    deletePlanaction,
    deleteProject,exportTextTraceCalcul,
} from '../services/projectService';
import '../App.css';
import RuleIcon from '@mui/icons-material/Rule';
import {PlanAction} from "../interfaces/PlanAction";

interface PlanActionTableProps {
    planActions: PlanAction[];
    onEditPlanAction: (planaction: PlanAction) => void;
    refreshPlanAction: () => void;
}

const PlanActionTable: React.FC<PlanActionTableProps> = ({ planActions, onEditPlanAction, refreshPlanAction }) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [planActionToDelete, setPlanActionToDelete] = useState<PlanAction | null>(null);
    const [isExportingText, setIsExportingText] = useState<number | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedPlanAction, setSelectedPlanAction] = useState<number[]>([]);

    const handleDelete = async (planactionId: number) => {
        try {
            await deletePlanaction(planactionId);
        } catch (error) {
            console.error('Erreur lors de la suppression du plan d\'action:', error);
        }
    };

    const openDeleteModal = (planaction: PlanAction) => {
        setPlanActionToDelete(planaction);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setPlanActionToDelete(null);
    };

    const confirmDelete = async () => {
        if (planActionToDelete) {
            try {
                await deleteProject(planActionToDelete.id);
                refreshPlanAction();
            } catch (error) {
                console.error('Erreur lors de la suppression du plan d\'action:', error);
            } finally {
                closeDeleteModal();
            }
        }
    };


    const handleExportTraceCalcul = async (planaction: PlanAction) => {
        setIsExportingText(planaction.id);
        try {
            await exportTextTraceCalcul(planaction.id, planaction.refId);
        } catch (error) {
            console.error('Erreur lors de l\'export du plan d\'action:', error);
        } finally {
            setIsExportingText(null);
        }
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedPlanAction(planActions.map(planAction => planAction.id));
        } else {
            setSelectedPlanAction([]);
        }
    };

    const handleSelectPlanAction = (planActionID: number) => {
        setSelectedPlanAction(prevSelected =>
            prevSelected.includes(planActionID)
                ? prevSelected.filter(id => id !== planActionID)
                : [...prevSelected, planActionID]
        );
    };

    // Pagination logic
    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedPlanaction = planActions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Paper elevation={3} sx={{ p: 3, width: '100%', position: 'relative' }}>

            {/* Panneau de filtres */}
            <Box mb={3} p={2} sx={{ border: '2px solid #4caf50', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    <FilterList sx={{ mr: 1, color: '#4caf50' }} />
                    Filtres
                </Typography>


            </Box>

            {planActions.length > 0 ? (
                <Box>

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                    >
                        {selectedPlanAction.length > 0 && (
                            <>
                                <Button
                                    variant="contained"
                                    color="error"
                                    sx={{
                                        position: 'relative',
                                        top: 8,
                                        mb: 3
                                    }}
                                    onClick={() => {
                                        selectedPlanAction.forEach((plan) => {
                                            handleDelete(plan);
                                            refreshPlanAction();
                                        });

                                    }}
                                >
                                    Supprimer {selectedPlanAction.length} {selectedPlanAction.length > 1 ? 'plans d\'actions' : 'plan d\'action'}
                                </Button>

                                <Button
                                    variant="contained"
                                    color="warning"
                                    sx={{
                                        position: 'relative',
                                        top: 8,
                                        mb: 3
                                    }}
                                    onClick={() => {
                                        // Action à réaliser sur les projets sélectionnés
                                        console.log('Action sur les plan d\'actions sélectionnés:', selectedPlanAction);
                                    }}
                                >
                                    Télécharger {selectedPlanAction.length} {selectedPlanAction.length > 1 ? 'plan d\'actions' : 'plan d\'action'}
                                </Button>
                            </>

                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                position: 'relative',
                                top: 8,
                                mb: 3,
                            }}
                            onClick={() => {
                                refreshPlanAction()
                            }}
                        >
                            Rechercher
                        </Button>
                    </Box>

                    <TableContainer component={Paper} sx={{ border: '2px solid #2196f3' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#2196f3' }} >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedPlanAction.length === planActions.length}
                                            indeterminate={false}//selectedPlanAction.length > 0 && selectedPlanAction.length < selectedPlanAction.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>ID</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Ref ID</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Type plan d'action</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Classe Énergétique Objectif</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Budget</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Montant d'aide</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Reste à charge</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedPlanaction.map((planaction) => (
                                    <TableRow
                                        key={planaction.id}
                                        sx={{
                                            backgroundColor: selectedPlanAction.includes(planaction.id) ? '#e3f2fd' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: selectedPlanAction.includes(planaction.id) ? '#e3f2fd' : '#f5f5f5', // Couleur de surbrillance
                                                cursor: 'pointer'
                                            }
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedPlanAction.includes(planaction.id)}
                                                onChange={() => handleSelectPlanAction(planaction.id)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">{planaction.id}</TableCell>
                                        <TableCell align="center">{planaction.refId}</TableCell>
                                        <TableCell align="center">{planaction.typePlan}</TableCell>
                                        <TableCell align="center">{planaction.classeEnergetiqueObjectique}</TableCell>
                                        <TableCell align="center">{planaction.budget}</TableCell>
                                        <TableCell align="center">{planaction.montantAides}</TableCell>
                                        <TableCell align="center">{planaction.resteACharge}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                onClick={() => handleExportTraceCalcul(planaction)}
                                                color="success"
                                                title={'Télécharger trace des calculs '}
                                                disabled={isExportingText === planaction.id}
                                            >
                                                {isExportingText === planaction.id ? <CircularProgress size={24} /> : <RuleIcon />}
                                            </IconButton>

                                            <IconButton onClick={() => openDeleteModal(planaction)} color="error" title={'Supprimer plan d\'action ID N°' + planaction.id}>
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={planActions.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            ) : (
                <p>Aucun plan d'action trouvé.</p>
            )}

            {/* Modal de confirmation de suppression */}
            <Dialog open={isDeleteModalOpen} onClose={closeDeleteModal}>
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir supprimer le plan d'action "{planActionToDelete?.classeEnergetiqueObjectique}" numéro Ademe "{planActionToDelete?.numeroAdeme}" ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteModal} color="primary">
                        Annuler
                    </Button>
                    <Button onClick={confirmDelete} color="error">
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default PlanActionTable;